import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CheckCircleOutlined, DeleteFilled, StopOutlined } from '@ant-design/icons';
import { useDispatch } from 'dva';
import { useIntl } from 'umi';

import { useMultiSelection, useRequestTracker } from '@/hooks';
import {
  getSelectedItemIds,
  handleConfigTableSort,
  isItemDisabled,
  isItemEnabled,
  resolveConfigBulkEffectName,
} from '@/utils/infrastructure';

import useConfigListController from './useConfigListController';
import { useScheduledExportNotification } from './useScheduledExportNotification';

/**
 * Maps DVA state for a configuration list page connected component.
 */
export function mapConfigListState(namespace) {
  return ({ [namespace]: modelState, global, loading }) => ({
    ...modelState,
    loading: loading.effects[`${namespace}/fetchList`],
    loadingExport: loading.effects['global/export'],
    global,
    bulkActionErrorData: modelState.bulkActionErrorData,
    showBulkActionErrors: modelState.showBulkActionErrors,
    scheduledTask: global.scheduledTask,
  });
}

/**
 * Page-level orchestration for Configuration list modules.
 */
export function useConfigListPage({
  namespace,
  exportRoute,
  buildColumns,
  columnActions,
  bulkEffectNames,
  defaultOrderBy = 'name asc',
  importMode = 'toggle',
  withBulkActions = true,
  withBulkActivateDeactivate = true,
  withBulkDelete = true,
  withImport = true,
  withExport = true,
  withAdd = true,
  internalTasks,
  extraActions,
  extraSelectionActions,
  list,
  pagination,
  loading,
  loadingExport,
  global,
  orderBy,
  detail,
  success,
  select,
  searchSelect,
  notSelectSearch,
  showImport,
  scheduledTask,
}) {
  useRequestTracker(namespace);
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { modalConfirm, globalFilters, pageSize, searchCriteria } = global;
  const globalFiltersForModule = globalFilters?.[namespace];
  const [internalTasksOpen, setInternalTasksOpen] = useState(false);

  const {
    selectedRowKeys,
    selectedRows,
    handleRowSelection,
    clearSelection,
    hasSelection,
  } = useMultiSelection();

  const {
    columns,
    loadDataPaginate,
    confirm,
    openDrawerCreateOrEdit,
    getExportParams,
  } = useConfigListController({
    modelName: namespace,
    defaultOrderBy,
    buildColumns,
    columnActions,
    select,
    searchSelect,
    notSelectSearch,
    orderBy,
    global,
    pagination,
    loading,
    list,
    globalFilters,
  });

  const bulkEffect = useCallback(
    (action) => resolveConfigBulkEffectName(namespace, action, bulkEffectNames),
    [namespace, bulkEffectNames],
  );

  useEffect(() => {
    return () => {
      dispatch({ type: `${namespace}/resetState` });
      dispatch({ type: 'global/resetGlobals' });
    };
  }, [dispatch, namespace]);

  useEffect(() => {
    if (!success) return;
    loadDataPaginate({ page: 1 });
    dispatch({
      type: `${namespace}/changeSuccess`,
      payload: { success: false },
    });
  }, [success, loadDataPaginate, dispatch, namespace]);

  useScheduledExportNotification(scheduledTask, dispatch);

  const handleSearch = useCallback(
    (searchValue) => {
      dispatch({ type: 'global/tableSearchState', payload: searchValue });
    },
    [dispatch],
  );

  const handleAdd = useCallback(() => {
    openDrawerCreateOrEdit();
  }, [openDrawerCreateOrEdit]);

  const toggleImportContainer = useCallback(() => {
    const payload = importMode === 'open' ? { show: true } : { show: !showImport };
    dispatch({
      type: `${namespace}/toggleImportComponent`,
      payload,
    });
  }, [dispatch, importMode, namespace, showImport]);

  const handleExport = useCallback(() => {
    if (!exportRoute) return;

    const ids = getSelectedItemIds(selectedRowKeys, list || []);
    if (ids.length > 0) {
      const { orderBy: exportOrderBy } = getExportParams();
      dispatch({
        type: 'global/export',
        payload: {
          route: exportRoute,
          httpMethod: 'post',
          data: ids,
          ...(exportOrderBy && { orderBy: exportOrderBy }),
        },
      });
      return;
    }

    dispatch({
      type: 'global/export',
      payload: {
        route: exportRoute,
        httpMethod: 'get',
        ...getExportParams(),
      },
    });
  }, [dispatch, exportRoute, getExportParams, selectedRowKeys, list]);

  const handleOpenInternalTasks = useCallback(() => {
    setInternalTasksOpen(true);
  }, []);

  const handleCloseInternalTasks = useCallback(() => {
    setInternalTasksOpen(false);
  }, []);

  const handlePageSize = useCallback(
    (item) => {
      dispatch({ type: 'global/changePageSizeState', payload: item.value });
    },
    [dispatch],
  );

  const confirmHandler = useCallback(() => {
    confirm(global.deleteId, detail);
  }, [confirm, global.deleteId, detail]);

  const handleBulkDelete = useCallback(
    async (ids) => {
      if (!ids || ids.length === 0) return;
      await dispatch({
        type: `${namespace}/${bulkEffect('delete')}`,
        payload: { ids },
      });
      clearSelection();
    },
    [dispatch, namespace, bulkEffect, clearSelection],
  );

  const handleDeleteFromSelection = useCallback(() => {
    const ids = getSelectedItemIds(selectedRowKeys, list || []);
    if (ids.length === 0) return;
    dispatch({
      type: 'global/modalConfirm',
      payload: { state: true, id: ids[0], modelDelete: namespace },
    });
  }, [selectedRowKeys, list, dispatch]);

  const confirmBulkDeleteHandler = useCallback(async () => {
    const ids = getSelectedItemIds(selectedRowKeys, list || []);
    if (ids.length === 0) return;
    dispatch({ type: 'global/modalConfirm', payload: { state: false, id: null } });
    await handleBulkDelete(ids);
  }, [selectedRowKeys, list, handleBulkDelete, dispatch]);

  const handleBulkActivate = useCallback(() => {
    const ids = getSelectedItemIds(selectedRowKeys, list || [], isItemDisabled);
    if (ids.length > 0) {
      dispatch({
        type: `${namespace}/${bulkEffect('activate')}`,
        payload: { ids },
      });
    }
    clearSelection();
  }, [selectedRowKeys, list, dispatch, namespace, bulkEffect, clearSelection]);

  const handleBulkDeactivate = useCallback(() => {
    const ids = getSelectedItemIds(selectedRowKeys, list || [], isItemEnabled);
    if (ids.length > 0) {
      dispatch({
        type: `${namespace}/${bulkEffect('deactivate')}`,
        payload: { ids },
      });
    }
    clearSelection();
  }, [selectedRowKeys, list, dispatch, namespace, bulkEffect, clearSelection]);

  const closeBulkActionErrorsDrawer = useCallback(() => {
    dispatch({
      type: `${namespace}/toggleBulkActionErrorsDrawer`,
      payload: { open: false },
    });
  }, [dispatch, namespace]);

  const handleTableChange = useCallback(
    (p, f, s) => {
      handleConfigTableSort({ modelName: namespace, dispatch, orderBy, defaultOrderBy }, p, f, s);
    },
    [dispatch, namespace, orderBy, defaultOrderBy],
  );

  /**
   * Module-contributed selection actions (e.g. bulk 2FA). Merged with the shared
   * enable/disable/delete defaults into a single SelectionBar `actions` array.
   */
  const resolvedExtraSelectionActions = useMemo(() => {
    if (!extraSelectionActions) return null;
    const ctx = {
      dispatch,
      formatMessage,
      namespace,
      selectedRowKeys,
      selectedRows,
      list,
      clearSelection,
    };
    const resolved =
      typeof extraSelectionActions === 'function'
        ? extraSelectionActions(ctx)
        : extraSelectionActions;
    return Array.isArray(resolved) ? resolved.filter(Boolean) : null;
  }, [
    extraSelectionActions,
    dispatch,
    formatMessage,
    namespace,
    selectedRowKeys,
    selectedRows,
    list,
    clearSelection,
  ]);

  const selectionBarProps = useMemo(() => {
    if (!withBulkActions || !hasSelection) return null;

    const baseProps = {
      selectedCount: selectedRowKeys.length,
      totalCount: list?.length || 0,
      selectedRowKeys,
      selectedRows,
      data: list || [],
      rowKey: 'id',
      onRowSelection: handleRowSelection,
    };

    // With module-contributed actions, build the full ordered `actions` array
    // (enable → disable → module actions → delete). SelectionBar prefers `actions`.
    if (resolvedExtraSelectionActions?.length) {
      const activateActions = withBulkActivateDeactivate
        ? [
            {
              key: 'enable',
              label: formatMessage({ id: 'global.enable' }),
              icon: <CheckCircleOutlined />,
              onClick: handleBulkActivate,
              visibilityCondition: (rows) => rows?.some((row) => isItemDisabled(row)) ?? false,
            },
            {
              key: 'disable',
              label: formatMessage({ id: 'global.disable' }),
              icon: <StopOutlined />,
              onClick: handleBulkDeactivate,
              visibilityCondition: (rows) => rows?.some((row) => isItemEnabled(row)) ?? false,
            },
          ]
        : [];

      const deleteActions = withBulkDelete
        ? [
            {
              key: 'delete',
              label: formatMessage({ id: 'global.button.delete' }),
              icon: <DeleteFilled />,
              onClick: handleDeleteFromSelection,
              danger: true,
            },
          ]
        : [];

      return {
        ...baseProps,
        actions: [...activateActions, ...resolvedExtraSelectionActions, ...deleteActions],
      };
    }

    return {
      ...baseProps,
      ...(withBulkDelete
        ? {
            onAction: handleDeleteFromSelection,
            actionLabel: formatMessage({ id: 'global.button.delete' }),
            actionType: 'danger',
          }
        : {}),
      ...(withBulkActivateDeactivate
        ? {
            onEnableAction: handleBulkActivate,
            enableActionLabel: formatMessage({ id: 'global.enable' }),
            onDisableAction: handleBulkDeactivate,
            disableActionLabel: formatMessage({ id: 'global.disable' }),
          }
        : {}),
    };
  }, [
    withBulkActions,
    withBulkActivateDeactivate,
    withBulkDelete,
    hasSelection,
    selectedRowKeys,
    selectedRows,
    list,
    handleRowSelection,
    handleDeleteFromSelection,
    handleBulkActivate,
    handleBulkDeactivate,
    formatMessage,
    resolvedExtraSelectionActions,
  ]);

  const actionContext = useMemo(
    () => ({
      dispatch,
      formatMessage,
      namespace,
      loadingExport,
      selectedRowKeys,
      list,
      pagination,
      clearSelection,
    }),
    [
      dispatch,
      formatMessage,
      namespace,
      loadingExport,
      selectedRowKeys,
      list,
      pagination,
      clearSelection,
    ],
  );

  const heroActions = useMemo(() => {
    const items = [];

    const resolvedExtra =
      typeof extraActions === 'function' ? extraActions(actionContext) : extraActions;
    const extraList = Array.isArray(resolvedExtra) ? resolvedExtra.filter(Boolean) : [];
    const exportExtras = extraList.filter((action) => action.type === 'export');
    const otherExtras = extraList.filter((action) => action.type !== 'export');

    if (withAdd) {
      items.push({
        key: `add-${namespace}`,
        type: 'add',
        onClick: handleAdd,
        iconOnly: true,
      });
    }

    if (withExport && exportRoute) {
      items.push({
        key: `export-${namespace}`,
        type: 'export',
        onClick: handleExport,
        loading: loadingExport,
        iconOnly: true,
      });
    } else {
      exportExtras.forEach((action, index) => {
        items.push({ iconOnly: true, key: `export-extra-${namespace}-${index}`, ...action });
      });
    }

    if (withImport) {
      items.push({
        key: `import-${namespace}`,
        type: 'import',
        onClick: toggleImportContainer,
        iconOnly: true,
      });
    }

    if (internalTasks?.listRoute) {
      items.push({
        key: `tasks-${namespace}`,
        type: 'tasks',
        onClick: handleOpenInternalTasks,
        iconOnly: true,
        authority: internalTasks.authority,
      });
    }

    otherExtras.forEach((action, index) => {
      items.push({ iconOnly: true, key: `extra-${namespace}-${index}`, ...action });
    });

    return items;
  }, [
    namespace,
    withAdd,
    handleAdd,
    withImport,
    withExport,
    exportRoute,
    toggleImportContainer,
    handleExport,
    loadingExport,
    internalTasks,
    handleOpenInternalTasks,
    extraActions,
    actionContext,
  ]);

  return {
    dispatch,
    formatMessage,
    columns,
    loadDataPaginate,
    handleSearch,
    handlePageSize,
    handleTableChange,
    heroActions,
    selectionBarProps,
    selectedRowKeys,
    handleRowSelection,
    modalConfirm,
    confirmHandler,
    confirmBulkDeleteHandler,
    closeBulkActionErrorsDrawer,
    pageSize,
    searchCriteria,
    globalFiltersForModule,
    internalTasksOpen,
    handleCloseInternalTasks,
  };
}

export default useConfigListPage;
